import { useCallback, useEffect, useMemo, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import MarketingCredits from '../MarketingCredits';
import AutomationRecipeGallery from '../AutomationRecipeGallery';
import AutomationsTable from '../AutomationsTable';
import AutomationEditorModal from '../AutomationEditorModal';
import { TemplateEmbedData } from '../CampaignTemplateDesigner/TemplateEmbed';
import useGetMarketingData from '../../../hooks/marketing/useGetMarketingData';
import useRecipeTemplate from '../../../hooks/marketing/useRecipeTemplate';
import useGetAutomationRecipes from '../../../hooks/marketing/useGetAutomationRecipes';
import useLoadingModal from '../../../hooks/global/useLoadingModal';
import useGetAutomations from '../../../hooks/marketing/useGetAutomations';
import useAutomationActions from '../../../hooks/marketing/useAutomationActions';
import useMarketingActionGate from '../../../hooks/marketing/useMarketingActionGate';
import useGetBusinessServices from '../../../hooks/businesses/useGetBusinessServices';
import {
  filterRecipesByServices,
  getAutomationsByRecipeId,
} from '../../../helpers/marketing/automations';
import { getAutomationRowElementId } from '../../../utils/table/automationsTable';
import {
  AutomationListItem,
  AutomationRecipe,
} from '../../../types/marketing/automations';

type EditorTarget =
  | {
      kind: 'recipe';
      recipe: AutomationRecipe;
      template: TemplateEmbedData;
    }
  | { kind: 'custom' };

const HIGHLIGHT_DURATION_MS = 2500;
const EMPTY_AUTOMATIONS: AutomationListItem[] = [];
const EMPTY_RECIPES: AutomationRecipe[] = [];

function AutomationsPanel() {
  const {
    data: automations = EMPTY_AUTOMATIONS,
    isPending: isPendingAutomations,
    isError,
  } = useGetAutomations();
  const { handlers, isPending } = useAutomationActions();
  const runMarketingAction = useMarketingActionGate();
  const { data: marketingData } = useGetMarketingData();
  const { data: recipes = EMPTY_RECIPES, isPending: isPendingRecipes } =
    useGetAutomationRecipes();
  const { services, isLoading: isLoadingServices } = useGetBusinessServices();
  const { openLoadingModal, closeLoadingModal } = useLoadingModal();

  const { mutate: resolveRecipeTemplate } = useRecipeTemplate({
    onError: message => toast.error(message),
  });

  const [editorTarget, setEditorTarget] = useState<EditorTarget | null>(null);
  const [highlightedAutomationId, setHighlightedAutomationId] = useState<
    string | null
  >(null);

  const automationsByRecipeId = useMemo(
    () => getAutomationsByRecipeId(automations),
    [automations],
  );

  const offeredRecipes = useMemo(
    () => filterRecipesByServices(recipes, services ?? []),
    [recipes, services],
  );

  const openEditor = useCallback(
    (target: EditorTarget) => runMarketingAction(() => setEditorTarget(target)),
    [runMarketingAction],
  );

  const onSetUpRecipe = useCallback(
    (recipe: AutomationRecipe) =>
      runMarketingAction(() => {
        const marketingBusinessId = marketingData?.id;
        if (!marketingBusinessId) {
          toast.error('Marketing is not set up yet');
          return;
        }
        openLoadingModal({
          header: 'Preparing your template',
          message: 'Please wait while the recipe is being set up',
        });
        resolveRecipeTemplate(
          { marketingBusinessId, recipe },
          {
            onSuccess: result =>
              setEditorTarget({
                kind: 'recipe',
                recipe,
                template: { id: result.template.id, url: result.embed.url },
              }),
            onSettled: () => closeLoadingModal(),
          },
        );
      }),
    [
      runMarketingAction,
      marketingData?.id,
      openLoadingModal,
      closeLoadingModal,
      resolveRecipeTemplate,
    ],
  );

  const onViewAutomation = useCallback((automationId: string) => {
    setHighlightedAutomationId(automationId);
    document
      .getElementById(getAutomationRowElementId(automationId))
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  useEffect(() => {
    if (!highlightedAutomationId) return undefined;
    const timeout = setTimeout(
      () => setHighlightedAutomationId(null),
      HIGHLIGHT_DURATION_MS,
    );
    return () => clearTimeout(timeout);
  }, [highlightedAutomationId]);

  return (
    <Stack spacing={3}>
      <MarketingCredits />
      <Stack spacing={1.5}>
        <Typography variant="h4">Recipes</Typography>
        <AutomationRecipeGallery
          recipes={offeredRecipes}
          automationsByRecipeId={automationsByRecipeId}
          isPending={
            isPendingAutomations || isPendingRecipes || isLoadingServices
          }
          isStatusUnavailable={isPendingAutomations || isError}
          onSetUpRecipe={onSetUpRecipe}
          onBuildYourOwn={() => openEditor({ kind: 'custom' })}
          onViewAutomation={onViewAutomation}
        />
      </Stack>
      <AutomationsTable
        automations={automations}
        isLoading={isPendingAutomations}
        isError={isError}
        handlers={handlers}
        disableActions={isPending}
        highlightedAutomationId={highlightedAutomationId}
      />
      {editorTarget && (
        <AutomationEditorModal
          isOpen
          onClose={() => setEditorTarget(null)}
          recipe={editorTarget.kind === 'recipe' ? editorTarget.recipe : null}
          template={
            editorTarget.kind === 'recipe' ? editorTarget.template : null
          }
        />
      )}
    </Stack>
  );
}

export default AutomationsPanel;
